// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The map. Its fill is a QUALITATIVE set encoding — which carriers are a real
// option in this region — not a magnitude ramp. "How many towers" is a hover
// statistic; "is there any choice here" is the question the colour answers.
//
// Boundaries are the real ABS ASGS 2021 SA3 file produced by the pipeline.
// Leaflet is contained by an `isolation: isolate` wrapper so its panes (which
// go up to z-index 1000) cannot paint over the modals and drawers.

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { TowerData, Region } from '../types';
import { CARRIER_ALIAS, CARRIER_COLOR, setColor, setLabel, SET_ORDER } from '../colors';
import { esc, num } from '../format';
import { gloss } from '../glossary';
import { openSite } from '../drawer';
import { legend, viewHead, setPressed } from './ui';

export function mount(el: HTMLElement, data: TowerData): () => void {
  const { meta, regions } = data;
  let useMocn = false;
  let filterSet: string | null = null;

  const setOf = (r: Region) => (useMocn ? r.setMocn : r.set);

  el.innerHTML = `
    ${viewHead({
      title: 'Who can actually reach you',
      question: 'If I live here, do I get a genuine choice of network — or only one?',
      blurb: `Colour shows which carriers are ${gloss('realoption', 'a real option')} in each ${gloss('sa3', 'region')}, judged on ${gloss('lowband', 'long-reach spectrum')} — the sub-1 GHz bands that actually carry a signal across country. Saturated colour means one network and no choice. Zoom in past the region level to see individual towers.`,
    })}
    <div class="map-controls">
      <label class="toggle">
        <input type="checkbox" data-mocn />
        <span>Count Optus–TPG ${gloss('mocn', 'network sharing')}</span>
      </label>
      <span class="map-note" data-note></span>
    </div>
    <div class="map-frame">
      <div class="map-canvas"></div>
    </div>
    <div data-legend-host></div>
    <p class="src-note">Boundaries: ABS ASGS 2021 SA3 (CC BY 4.0). Basemap © CARTO, © OpenStreetMap contributors. Sites: ACCC (CC BY 2.5 AU), ${meta.latestYear} snapshot.</p>
  `;

  const legendHost = el.querySelector('[data-legend-host]') as HTMLElement;
  const note = el.querySelector('[data-note]') as HTMLElement;
  const canvas = el.querySelector('.map-canvas') as HTMLElement;

  const map = L.map(canvas, { minZoom: 3, maxZoom: 13, zoomControl: true, scrollWheelZoom: false });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO',
    subdomains: 'abcd',
    minZoom: 3,
    maxZoom: 13,
  }).addTo(map);

  let geoLayer: L.GeoJSON | null = null;
  const markerLayer = L.layerGroup();
  let markersBuilt = false;

  function tooltipFor(r: Region): string {
    const rows = meta.carriers
      .map((c) => {
        const s = r.car[c];
        const live = setOf(r)[meta.carriers.indexOf(c)] === '1';
        return `<tr class="${live ? '' : 'dim'}">
          <td><span class="dot" style="background:${CARRIER_COLOR[c]}"></span>${esc(CARRIER_ALIAS[c])}</td>
          <td>${num(s.low)}</td><td>${num(s.sites)}</td>
          <td>${s.low === 0 ? '<em class="bad">no long reach</em>' : live ? 'real option' : 'token presence'}</td>
        </tr>`;
      })
      .join('');
    const extra = useMocn && r.mocn ? `<p class="tip-note">+${num(r.mocn)} shared Optus–TPG sites counted for TPG.</p>` : '';
    return `<div class="map-tip">
      <strong>${esc(r.name)}</strong><span class="tip-sub">${esc(r.state)} · ${num(r.pop)} residents · ${num(r.areaKm2)} km²</span>
      <table><thead><tr><th></th><th>Long&nbsp;reach</th><th>All sites</th><th></th></tr></thead><tbody>${rows}</tbody></table>
      ${r.coFunded ? `<p class="tip-note">${num(r.coFunded)} site${r.coFunded === 1 ? '' : 's'} here were publicly co-funded.</p>` : ''}
      ${extra}
      <p class="tip-go">Click for the full region comparison</p>
    </div>`;
  }

  function styleFor(r: Region): L.PathOptions {
    const s = setOf(r);
    const dimmed = filterSet !== null && s !== filterSet;
    return {
      fillColor: setColor(s),
      fillOpacity: dimmed ? 0.12 : 0.85,
      color: '#ffffff',
      weight: 0.7,
      opacity: dimmed ? 0.3 : 1,
    };
  }

  function renderLegend(): void {
    const counts = new Map<string, number>();
    for (const r of regions) counts.set(setOf(r), (counts.get(setOf(r)) || 0) + 1);
    const items = SET_ORDER.filter((s) => counts.get(s)).map((s) => ({
      color: setColor(s),
      label: setLabel(s, meta.carriers),
      key: s,
      tip: `${num(counts.get(s) || 0)} regions${filterSet === s ? ' — click again to clear' : ' — click to isolate'}`,
    }));
    legendHost.innerHTML = legend(items, { interactive: true });
    setPressed(legendHost.querySelectorAll('[data-legend]'), (b) => b.getAttribute('data-legend') === filterSet);
    legendHost.querySelectorAll('[data-legend]').forEach((b) => {
      const key = b.getAttribute('data-legend')!;
      b.addEventListener('click', () => {
        filterSet = filterSet === key ? null : key;
        geoLayer?.setStyle((f) => styleFor(regions[data.regionByCode.get((f as GeoJSON.Feature).properties!.code)!]));
        renderLegend();
        updateNote();
      });
    });
  }

  function updateNote(): void {
    const single = regions.filter((r) => setOf(r).split('').filter((c) => c === '1').length === 1);
    const pop = single.filter((r) => r.pop >= 1000).reduce((n, r) => n + r.pop, 0);
    const area = single.reduce((n, r) => n + r.areaKm2, 0);
    const totalArea = regions.reduce((n, r) => n + r.areaKm2, 0);
    const scope = filterSet
      ? `Showing ${num(regions.filter((r) => setOf(r) === filterSet).length)} regions where ${setLabel(filterSet, meta.carriers).toLowerCase()}. `
      : '';
    note.innerHTML = `${scope}<strong>${num(single.length)}</strong> regions have just one real option — ${((area / totalArea) * 100).toFixed(1)}% of Australia's land area, and about ${num(pop)} people.`;
  }

  // Individual towers appear once you are zoomed in far enough that they mean
  // something; drawing 25,930 circle markers at national zoom is a grey smear.
  function buildMarkers(): void {
    if (markersBuilt) return;
    markersBuilt = true;
    const s = data.sites;
    for (let i = 0; i < s.n; i++) {
      const carrier = meta.carriers[s.mno[i]];
      const funded = s.fund[i] >= 0;
      const m = L.circleMarker([s.lat[i], s.lon[i]], {
        radius: 4,
        fillColor: CARRIER_COLOR[carrier],
        fillOpacity: 0.85,
        color: funded ? '#111827' : '#ffffff',
        weight: funded ? 1.6 : 0.8,
      });
      m.bindTooltip(
        `${CARRIER_ALIAS[carrier]} · site ${s.rfnsa[i] || '—'}${funded ? ` · publicly co-funded (${s.programs[s.fund[i]]})` : ''}`,
        { direction: 'top', opacity: 0.95 },
      );
      m.on('click', () => openSite(data, i));
      markerLayer.addLayer(m);
    }
  }

  function syncMarkers(): void {
    if (map.getZoom() >= 9) {
      buildMarkers();
      if (!map.hasLayer(markerLayer)) markerLayer.addTo(map);
    } else if (map.hasLayer(markerLayer)) {
      map.removeLayer(markerLayer);
    }
  }

  function drawRegions(geo: GeoJSON.FeatureCollection): void {
    if (geoLayer) map.removeLayer(geoLayer);
    geoLayer = L.geoJSON(geo, {
      attribution: 'Boundaries: ABS ASGS 2021 (CC BY 4.0)',
      style: (f) => styleFor(regions[data.regionByCode.get((f as GeoJSON.Feature).properties!.code)!]),
      onEachFeature: (f, lyr) => {
        const ri = data.regionByCode.get(f.properties!.code);
        if (ri === undefined) return;
        const r = regions[ri];
        lyr.bindTooltip(tooltipFor(r), { sticky: true, className: 'map-tip-wrap' });
        lyr.on({
          mouseover: () => (lyr as L.Path).setStyle({ weight: 2.4, color: '#0f172a' }),
          mouseout: () => geoLayer?.resetStyle(lyr as L.Path),
          click: () => {
            location.hash = `view=region&sa3=${r.code}`;
          },
        });
      },
    }).addTo(map);
  }

  const controller = new AbortController();
  fetch('data/sa3.geojson', { signal: controller.signal })
    .then((r) => {
      if (!r.ok) throw new Error(`boundaries → HTTP ${r.status}`);
      return r.json();
    })
    .then((geo: GeoJSON.FeatureCollection) => {
      drawRegions(geo);
      renderLegend();
      updateNote();
      // Open on the mainland. Fitting the layer's own bounds would include
      // Christmas Island (105°E) and Norfolk Island (168°E), which shrinks
      // Australia to a smudge in the middle of a lot of ocean. Those regions
      // are still on the map — just not in the opening frame.
      const bounds = L.latLngBounds([-44.2, 112.5], [-9.5, 154.2]);
      // Fit exactly once, as soon as the container genuinely has height.
      // Running it a second time after a later resize re-frames the map under
      // the reader for no reason.
      let fitted = false;
      const fit = () => {
        if (fitted || canvas.clientHeight <= 50) return;
        fitted = true;
        map.invalidateSize();
        map.fitBounds(bounds, { padding: [8, 8] });
      };
      const ro = new ResizeObserver(() => {
        fit();
        if (fitted) ro.disconnect();
      });
      ro.observe(canvas);
      setTimeout(fit, 400);
    })
    .catch((err) => {
      if ((err as Error).name === 'AbortError') return;
      canvas.innerHTML = `<div class="error">Could not load the region boundaries. ${esc((err as Error).message)}</div>`;
    });

  map.on('zoomend', syncMarkers);

  const mocnBox = el.querySelector('[data-mocn]') as HTMLInputElement;
  mocnBox.addEventListener('change', () => {
    useMocn = mocnBox.checked;
    filterSet = null;
    geoLayer?.setStyle((f) => styleFor(regions[data.regionByCode.get((f as GeoJSON.Feature).properties!.code)!]));
    geoLayer?.eachLayer((lyr) => {
      const code = ((lyr as unknown as { feature: GeoJSON.Feature }).feature).properties!.code as string;
      const ri = data.regionByCode.get(code);
      if (ri !== undefined) (lyr as L.Path).setTooltipContent(tooltipFor(regions[ri]));
    });
    renderLegend();
    updateNote();
  });

  el.querySelector('[data-mocn]')?.setAttribute('aria-label', 'Count Optus and TPG shared network sites');

  return () => {
    controller.abort();
    map.off();
    map.remove();
  };
}
